import * as moment from 'moment';
import {Moment} from 'moment';
import {Stack} from 'immutable';
import {IcePhenomena} from './ice-phenomena';

export interface IParsedKN15Telegram {
  telegramText:string;
  sectionZero:IParsedSectionZero;
  sectionOne:IParsedSectionOne;
  sectionThree?:IParsedSectionThree;
  sectionSix?:IParsedSectionSix;
  sectionEight?:IParsedSectionEight;
}

export interface IParsedSectionZero {
  stationCode:string;
  date?:Moment;
  sectionCode?:number;
}

export interface IIcePhenomena {
  code:number;
  intensity?:number;
}

export interface ITemperatures {
  waterTemperature:number;
  airTemperature?:number;
}

export interface IParsedSectionOne {
  waterLevelAtEight:number;
  waterLevelAtTwenty:number;
  waterLevelTrend:number;
  icePhenomenae?:Array<IIcePhenomena>;
  temperatures?:ITemperatures;
}

export interface IParsedSectionThree {
  waterLevelAverage:number;
}

export interface IParsedSectionSix {
  date:Moment,
  discharge:number,
  waterLevel:number,
  freeRiverArea?:number,
  maximumDepth?:number
}

export interface IParsedSectionEight {
  year: number,
  currentDate: Moment
  month:number,
  decade: number,
  precipitation:number,
  precipitationChecksum:number,
  airTemp: number,
  siteId?: number
}

export interface IKN15ParseService {
  parse(telegram:string):IParsedKN15Telegram;
}

export class KN15ParseError {
  constructor(public messageId:string,
              public badToken:string,
              public tokens:Stack<string>) {
  }
}

export class KN15ParseService implements IKN15ParseService {
  static STATION_CODE_REGEXP = /^[0-9]{5}$/;
  static isMeteoStationDataOnly: boolean;

  parse(telegram:string):IParsedKN15Telegram {
    const parsedTelegram:IParsedKN15Telegram = {
      telegramText: telegram,
      sectionZero: null,
      sectionOne: null,
    };
    KN15ParseService.isMeteoStationDataOnly = false;
    if (telegram.endsWith('=')) {
      telegram = telegram.slice(0, -1);
    }
    if (telegram.indexOf('//') != -1) {
      telegram = telegram.replace('//', '');
    }
    let tokens = Stack(telegram.trim().split(/\s+/));
    
    if (tokens.get(1) && tokens.get(1).indexOf('988') != -1) {
      KN15ParseService.isMeteoStationDataOnly = true;
    }

    const badToken = tokens.find(token => token.length != 5 && token.length != 3);
    if (badToken) {
      throw new KN15ParseError('TOO_SHORT_TOKEN',
          badToken, tokens)
    }

    let parsedSectionZero:IParsedSectionZero;
    ({tokens, parsedSectionZero} = KN15ParseService.parseSectionZero(tokens));
    parsedTelegram.sectionZero = parsedSectionZero;
    if (parsedSectionZero.sectionCode != 1 &&
        parsedSectionZero.sectionCode != 2 &&
        parsedSectionZero.sectionCode != null) {
      // TODO: Support more codes.
      throw new KN15ParseError('UNSUPPORTED_SECTION_CODE', '', tokens);
    }

    if (!KN15ParseService.isMeteoStationDataOnly) {
      let parsedSectionOne:IParsedSectionOne;
      ({tokens, parsedSectionOne} = KN15ParseService.parseSectionOne(tokens));
      parsedTelegram.sectionOne = parsedSectionOne;
    }

    if (parsedSectionZero && parsedSectionZero.sectionCode == 2) {
      while (tokens.size) {
        const token = tokens.peek();
        switch (token.slice(0, 3)) {
          case '933':
            let parsedSectionThree:IParsedSectionThree;
            ({
              tokens,
              parsedSectionThree
            } = KN15ParseService.parseSectionThree(tokens));
            parsedTelegram.sectionThree = parsedSectionThree;
            break;
          case '966':
            let parsedSectionSix:IParsedSectionSix;
            ({
              tokens,
              parsedSectionSix
            } = KN15ParseService.parseSectionSix(tokens));
            parsedTelegram.sectionSix = parsedSectionSix;
            break;
          case '988':
            let parsedSectionEight:IParsedSectionEight;
            ({
              tokens,
              parsedSectionEight
            } = KN15ParseService.parseSectionEight(tokens));
            parsedTelegram.sectionEight = parsedSectionEight;
            break;
          default:
            // TODO: Support more codes.
            throw new KN15ParseError('UNSUPPORTED_SECTION', token, tokens);
        }
      }
    } else if (parsedSectionZero && parsedSectionZero.sectionCode != 2 || KN15ParseService.isMeteoStationDataOnly) {
      while (tokens.size) {
        const token = tokens.peek(); 
        switch (token.slice(0, 3)) {
          case '988':
            let parsedSectionEight:IParsedSectionEight;
            ({
              tokens,
              parsedSectionEight
            } = KN15ParseService.parseSectionEight(tokens));
            parsedTelegram.sectionEight = parsedSectionEight;
            break;
          default:
            // TODO: Support more codes.
            throw new KN15ParseError('UNSUPPORTED_SECTION', token, tokens);
        }
      }
    }


    return parsedTelegram;
  }

  private static parseSectionZero(tokens:Stack<string>) {
    // Groups MMMM and BBiii
    let maxStationCodeTries = 2;
    let parsedStationCode:string;
    let token:string;
    while (maxStationCodeTries > 0 && !parsedStationCode) {
      token = tokens.peek();
      if (KN15ParseService.STATION_CODE_REGEXP.test(token)) {
        parsedStationCode = token;
      }
      tokens = tokens.pop();
      maxStationCodeTries--;
    }
    if (!parsedStationCode) {
      throw new KN15ParseError('NO_STATION_CODE', token, tokens);
    }

    if (KN15ParseService.isMeteoStationDataOnly) {
      return {
        tokens: tokens,
        parsedSectionZero: {
          stationCode: parsedStationCode,
          date: null,
          sectionCode: null
        }
      };
    }

    // Group YYGGn
    const dateToken = tokens.peek();
    
    tokens = tokens.pop();
    const yyPart = dateToken.slice(0, 2);
    const ggPart = dateToken.slice(2, 4);
    const nPart = dateToken.slice(4);

    const dayInMonth = Number.parseInt(yyPart);
    if (Number.isNaN(dayInMonth)) {
      throw new KN15ParseError('INVALID_DAY_IN_MONTH',
          dateToken, tokens);
    }
    if (ggPart != '08') {
      // TODO: Support more codes.
      throw new KN15ParseError('UNSUPPORTED_HOUR_CODE',
          dateToken, tokens);
    }
    let parsedDate = moment().date(dayInMonth);
    if (parsedDate.isAfter(moment().endOf('month'))) {
      // Carried over.
      parsedDate = moment().subtract(1, 'month').endOf('month');
    }
    if (parsedDate.isAfter(moment())) {
      parsedDate.subtract(1, 'month');
    }
    parsedDate.hour(8).minute(0).second(0).millisecond(0);
    if (!parsedDate.isValid()) {
      throw new KN15ParseError('OUT_OF_RANGE_DAY_IN_MONTH',
          dateToken, tokens);
    }

    const parsedSectionCode = Number.parseInt(nPart);
    if (Number.isNaN(parsedSectionCode)) {
      throw new KN15ParseError('INVALID_SECTION_CODE',
          dateToken, tokens);
    }

    return {
      tokens: tokens,
      parsedSectionZero: {
        stationCode: parsedStationCode,
        date: parsedDate,
        sectionCode: parsedSectionCode
      }
    };
  }

  private static parseSectionOne(tokens:Stack<string>) {
    // Group 1HHHH
    let token = tokens.peek();
    if (!(token && token.startsWith('1'))) {
      throw new KN15ParseError('INVALID_GROUP_ONE', token, tokens);
    }
    let waterLevelAtEight = Number.parseInt(token.slice(1));
    if (Number.isNaN(waterLevelAtEight)) {
      throw new KN15ParseError('INVALID_WATER_LEVEL',
          token, tokens);
    }
    waterLevelAtEight = waterLevelAtEight > 5000
        ? 5000 - waterLevelAtEight
        : waterLevelAtEight;
    tokens = tokens.pop();

    // Group 2HHHK
    token = tokens.peek();
    if (!(token && token.startsWith('2'))) {
      throw new KN15ParseError('INVALID_GROUP_TWO', token, tokens);
    }
    let waterLevelTrend = Number.parseInt(token.slice(1, 4));
    const waterLevelTrendSign = Number.parseInt(token.slice(4));
    if (Number.isNaN(waterLevelTrend) ||
        [0, 1, 2].indexOf(waterLevelTrendSign) == -1) {
      throw new KN15ParseError('INVALID_TREND', token, tokens);
    }
    waterLevelTrend = waterLevelTrendSign == 2
        ? -waterLevelTrend
        : waterLevelTrend;
    tokens = tokens.pop();

    // Group 3HHHH
    token = tokens.peek();
    if (!(token && token.startsWith('3'))) {
      throw new KN15ParseError('INVALID_GROUP_THREE',
          token, tokens);
    }
    let waterLevelAtTwenty = Number.parseInt(token.slice(1));
    if (Number.isNaN(waterLevelAtTwenty)) {
      throw new KN15ParseError('INVALID_WATER_LEVEL',
          token, tokens);
    }
    waterLevelAtTwenty = waterLevelAtTwenty > 5000
        ? 5000 - waterLevelAtTwenty
        : waterLevelAtTwenty;
    tokens = tokens.pop();

    let temperatures:ITemperatures = null;
    let icePhenomenae:Array<IIcePhenomena> = [];
    token = tokens.peek();

    while (tokens.size && !token.startsWith('9')) {
      // Group 4ttTT
      if (token.startsWith('4')) {
        let waterTemperature = Number.parseInt(token.slice(1,3));
        let airTemperature = Number.parseInt(token.slice(3));

        if (Number.isNaN(waterTemperature)) {
          throw new KN15ParseError('INVALID_TEMPERATURE',
              token, tokens);
        }
        waterTemperature /= 10;

        if (!Number.isNaN(airTemperature) && airTemperature > 50){
          airTemperature -= 50;
          airTemperature = -airTemperature;
        }

        if (Number.isNaN(airTemperature)){
          temperatures = {
            waterTemperature
          };          
        } else {
          temperatures = {
            waterTemperature,
            airTemperature
          };
        }
      }

      // Group 5EEii or 5EEEE
      if (token.startsWith('5')) {
        let icePhenomena:IIcePhenomena = null;
        const icePhenomenaCode = Number.parseInt(token.slice(1, 3));
        const intensity = Number.parseInt(token.slice(3));
        if (Number.isNaN(icePhenomenaCode) || Number.isNaN(intensity)) {
          throw new KN15ParseError('INVALID_ICE_PHENOMENA',
              token, tokens);
        }
        if (!IcePhenomena.verifyIcePhenomena(icePhenomenaCode, intensity)) {
          throw new KN15ParseError('INVALID_ICE_PHENOMENA',
              token, tokens);
        }
        if (icePhenomenaCode == intensity) {
          icePhenomena = {
            code: icePhenomenaCode
          };
        } else {
          icePhenomena = {
            code: icePhenomenaCode,
            intensity
          };
        }
        icePhenomenae.push(icePhenomena);
      }
      tokens = tokens.pop();
      token = tokens.peek();
    }

    return {
      tokens: tokens,
      parsedSectionOne: {
        waterLevelAtEight,
        waterLevelAtTwenty,
        waterLevelTrend,
        icePhenomenae,
        temperatures
      }
    };
  }

  private static parseSectionThree(tokens:Stack<string>) {
    tokens = tokens.pop();
    // TODO: Do something with the first token.

    // Group 1HHHH
    let token = tokens.peek();
    if (!(token && token.startsWith('1'))) {
      throw new KN15ParseError('INVALID_GROUP_ONE', token, tokens);
    }
    let waterLevelAverage = Number.parseInt(token.slice(1));
    waterLevelAverage = waterLevelAverage > 5000
        ? 5000 - waterLevelAverage
        : waterLevelAverage;
    tokens = tokens.pop();

    token = tokens.peek();
    while (tokens.size && !token.startsWith('9')) {
      tokens = tokens.pop();
      token = tokens.peek();
    }

    return {
      tokens: tokens,
      parsedSectionThree: {
        waterLevelAverage: waterLevelAverage
      }
    };
  }

  private static parseSectionSix(tokens:Stack<string>) {
    const parsedSectionSix:IParsedSectionSix = {
      waterLevel: null,
      discharge: null,
      date: null
    };
    // Group 966MM
    let token = tokens.peek();
    const monthString = token.slice(3);
    // Javascript months are zero-indexed.
    let month = Number.parseInt(monthString) - 1;
    if (Number.isNaN(month) || month > 11 || month < 0) {
      throw new KN15ParseError('INVALID_MONTH', token, tokens);
    }
    tokens = tokens.pop();

    // Group 1HHHH
    token = tokens.peek();
    if (!(token && token.startsWith('1'))) {
      throw new KN15ParseError('INVALID_GROUP_ONE', token, tokens);
    }
    const waterLevel = Number.parseInt(token.slice(1));
    parsedSectionSix.waterLevel = waterLevel > 5000
        ? 5000 - waterLevel
        : waterLevel;
    tokens = tokens.pop();

    // Group 2kQQQ
    token = tokens.peek();
    if (!(token && token.startsWith('2'))) {
      throw new KN15ParseError('INVALID_GROUP_TWO_DISCHARGE', token, tokens);
    }
    const dischargeSignficand = Number.parseInt(token.slice(2));
    const dischargeExponent = Number.parseInt(token.charAt(1));
    if (Number.isNaN(dischargeSignficand) || Number.isNaN(dischargeExponent)) {
      throw new KN15ParseError('INVALID_DISCHARGE_VALUE', token, tokens);
    }
    tokens = tokens.pop();
    parsedSectionSix.discharge = dischargeSignficand *
        Math.pow(10, dischargeExponent - 3);

    // Group 3kFFF
    token = tokens.peek();
    if (token && token.startsWith('3')) {
      const areaSignificand = Number.parseInt(token.slice(2));
      const areaExponent = Number.parseInt(token.charAt(1));
      if (Number.isNaN(dischargeSignficand) || Number.isNaN(dischargeExponent)) {
        throw new KN15ParseError('INVALID_FREE_RIVER_AREA_VALUE',
            token, tokens);
      }
      tokens = tokens.pop();
      parsedSectionSix.freeRiverArea = areaSignificand *
          Math.pow(10, areaExponent - 3);
    }

    // Group 4hhhh
    token = tokens.peek();
    if (token && token.startsWith('4')) {
      const maximumDepth = Number.parseInt(token.slice(1));
      if (Number.isNaN(maximumDepth)) {
        throw new KN15ParseError('INVALID_MAXIMUM_DEPTH_VALUE', token, tokens);
      }
      tokens = tokens.pop();
      parsedSectionSix.maximumDepth = maximumDepth;
    }

    // Group 5YYGG
    token = tokens.peek();
    if (!(token && token.startsWith('5'))) {
      throw new KN15ParseError('INVALID_GROUP_FIVE', token, tokens);
    }
    const yyPart = token.slice(1, 3);
    const dayInMonth = Number.parseInt(yyPart);
    let date = moment().month(month).date(dayInMonth);
    if (!date.isValid()) {
      throw new KN15ParseError('OUT_OF_RANGE_DAY_IN_MONTH', token, tokens);
    }
    const ggPart = token.slice(3);
    const hour = Number.parseInt(ggPart);
    date.hour(hour).minute(0).second(0).millisecond(0);
    if (!date.isValid()) {
      throw new KN15ParseError('OUT_OF_RANGE_HOUR', token, tokens);
    }
    if (date.isAfter(moment())) {
      date = date.subtract(1, 'year');
    }
    tokens = tokens.pop();
    parsedSectionSix.date = date;

    token = tokens.peek();
    while (tokens.size && !token.startsWith('9')) {
      tokens = tokens.pop();
      token = tokens.peek();
    }

    return {
      tokens: tokens,
      parsedSectionSix: parsedSectionSix
    };
  }

  private static parseSectionEight(tokens:Stack<string>) {
    const parsedSectionEight:IParsedSectionEight = {
      month: null,
      decade: null,
      year: Number.parseInt(moment().format('YYYY')),
      currentDate: moment(),
      precipitation: null,
      precipitationChecksum: null,
      airTemp:null
    };

    // Group 988MM
    let token = tokens.peek();
    const monthString = token.slice(3);
    // Javascript months are zero-indexed.
    let month = Number.parseInt(monthString) - 1;
    if (Number.isNaN(month) || month > 11 || month < 0) {
      throw new KN15ParseError('INVALID_MONTH', token, tokens);
    }
    tokens = tokens.pop();

    // Group 1
    token = tokens.peek();
    if (!(token && token.startsWith('1'))) {
      throw new KN15ParseError('INVALID_GROUP_ONE', token, tokens);
    }
    const decade = Number.parseInt(token.slice(1));
    let formattedDecade: number;
    switch (decade) {
      case 11:
        formattedDecade = 1;
        break;
      case 22:
        formattedDecade = 2;
        break;
      case 33:
        formattedDecade = 3;
        break;
      case 30:
        formattedDecade = 4;
        break;
      default:
        // TODO: Support more codes.
        throw new KN15ParseError('UNSUPPORTED_SECTION', token, tokens);
    }
    parsedSectionEight.month = Number.parseInt(moment().month(month).format('M'));
    parsedSectionEight.decade = formattedDecade;
    parsedSectionEight.year = Number.parseInt(moment().format('Y'));
    tokens = tokens.pop();


    // Group 2
    token = tokens.peek();
    if (token && token.startsWith('2')) {
      parsedSectionEight.precipitation = Number.parseInt(token.slice(1, -1));
      parsedSectionEight.precipitationChecksum = Number.parseInt(token.slice(4));
      tokens = tokens.pop();
    }

    // Group 3
    token = tokens.peek();
    if (token && token.startsWith('3')) {
      const airTempSign = Number.parseInt(token.slice(1, -3)) === 1 ? -1 : 1;
      const airTemp = Number.parseInt(token.slice(2)) * 0.1;
      parsedSectionEight.airTemp = airTemp * airTempSign;
      tokens = tokens.pop();
    }

    return {
      tokens: tokens,
      parsedSectionEight: parsedSectionEight
    };
  }
}



/** WEBPACK FOOTER **
 ** ./app/operational/input/kn15-telegram/kn15-parse.service.ts
 **/